import React from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { performance } from '@ynap/clientside-monitoring';
import { useAppState, useConfig } from '@ynap/immutables-provider';
import { ProductSummary, ProductSummaryWithAddToBag, ProductSummaryWithQuickAdd, ProductSummaryWithOverlay } from '@ynap/product-summary';
import { linkRenderer } from '@ynap/router';

const bem = new BEMHelper('RecommendationsProducts');

const RecommendationsProduct = ({
    addToCart,
    CTAButtonsState,
    details,
    doubleImageModifier,
    doubleImageWithoutPadding,
    drawerTracking,
    handleOverlayProductIdChange,
    index,
    inProgress = false,
    isLastAdded = false,
    isWishlistRecommendations,
    isWithAddToBag = false,
    messages,
    modifier,
    noDescription,
    omnibusAlignment,
    onClick = () => {},
    onRemove,
    overlayProductId,
    positionDrawer,
    recommendedProduct,
    reducedMode,
    setIsPhoneOverlay,
    tracking,
    type,
    withQuickAdd = false,
}) => {
    if (!recommendedProduct || Object.keys(recommendedProduct).length === 0) return null;

    const config = useConfig();
    const urlConfig = config?.global?.urlConfig;
    const pageRoutes = config?.head?.routesAllowlist?.pageRoutes;
    const { badge, consideredBadge, description, designer, href, image = {}, partNumber, price, productId } = recommendedProduct;
    const { alt, primary, primarySrcset, secondary, secondarySrcset } = image;
    const { addToCart: addToBagState = {}, country, locale } = useAppState();

    const originStrippedHref = href.replace(window.location.origin, '');

    const WithLink = ({ children }) =>
        linkRenderer(
            urlConfig,
            pageRoutes,
            locale,
            originStrippedHref,
            children,
            () => {
                onClick(recommendedProduct, index + 1);
                performance.intTiming.markSpaInteractionTime('pdp_to_pdp');
                drawerTracking?.();
                positionDrawer?.();
            },
            bem('product', [type, reducedMode ? 'reducedMode' : ''], { isWithAddToBag }),
            false,
            window.location.hostname,
        );

    return (
        <>
            {isWithAddToBag ? (
                <WithLink>
                    <ProductSummaryWithAddToBag
                        addToCart={addToCart}
                        addToCartError={addToBagState.error}
                        addToCartLoading={addToBagState.loading}
                        classNameButton={bem('addToBagBtn')}
                        messages={messages}
                        openProductDetail={() => tracking.ymal(recommendedProduct.partNumber)}
                        skuPartNumber={recommendedProduct.partNumber}
                        product={{
                            ...recommendedProduct,
                            imgSrc: {
                                alt,
                                primary,
                                primarySrcset,
                                secondary,
                                secondarySrcset,
                            },
                            designerName: designer,
                            name: description,
                            partNumber,
                            href,
                            price,
                            badge,
                            onSale: false,
                            consideredBadge,
                            isBuyable: !addToBagState.loading,
                        }}
                    />
                </WithLink>
            ) : withQuickAdd ? (
                <ProductSummaryWithQuickAdd
                    addToCart={addToCart}
                    classNameButton={bem('a')}
                    inProgress={inProgress}
                    isLastAdded={isLastAdded}
                    messages={messages}
                    openProductDetail={() => tracking.ymal(recommendedProduct.partNumber)}
                    skuPartNumber={recommendedProduct.partNumber}
                    product={{
                        ...recommendedProduct,
                        imgSrc: {
                            alt,
                            primary,
                            primarySrcset,
                            secondary,
                            secondarySrcset,
                        },
                        designerName: designer,
                        name: description,
                        partNumber,
                        href,
                        price,
                        badge,
                        onSale: false,
                        consideredBadge,
                        isBuyable: !addToBagState.loading,
                    }}
                />
            ) : isWishlistRecommendations ? (
                <ProductSummaryWithOverlay
                    CTAButtonsState={CTAButtonsState}
                    handleOverlayProductIdChange={handleOverlayProductIdChange}
                    imageSrcsetSize="(max-width: 1024px) 40vw, 20vw" // TODO: https://jira.nap/browse/SHOP-519
                    isWishlist={isWishlistRecommendations}
                    locale={locale}
                    messages={messages}
                    modifier={modifier}
                    nameModifier={noDescription ? 'noDescriptionStyle' : 'oneLine'}
                    overlayProductId={overlayProductId}
                    setIsPhoneOverlay={setIsPhoneOverlay}
                    type={type}
                    product={{
                        ...recommendedProduct,
                        imgSrc: {
                            alt,
                            primary,
                            primarySrcset,
                            secondary,
                            secondarySrcset,
                        },
                        designerName: designer,
                        name: description,
                        productId,
                        partNumber,
                        href,
                        price,
                        badge,
                        onSale: false,
                        consideredBadge,
                    }}
                />
            ) : (
                <WithLink>
                    <ProductSummary
                        country={country}
                        details={details}
                        doubleImageModifier={doubleImageModifier}
                        doubleImageWithoutPadding={doubleImageWithoutPadding}
                        imageSrcsetSize="(max-width: 1024px) 40vw, 20vw" // TODO: https://jira.nap/browse/SHOP-519
                        locale={locale}
                        messages={messages}
                        modifier={modifier}
                        nameModifier={noDescription ? 'noDescriptionStyle' : 'oneLine'}
                        omnibusAlignment={omnibusAlignment}
                        type={type}
                        imageOverlayElement={
                            onRemove && (
                                <div className={bem('buttonContainer')}>
                                    <button
                                        className={bem('removeButton')}
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            event.preventDefault();
                                            onRemove(recommendedProduct);
                                        }}
                                    />
                                </div>
                            )
                        }
                        product={{
                            imgSrc: {
                                alt,
                                primary,
                                primarySrcset,
                                secondary,
                                secondarySrcset,
                            },
                            designerName: designer,
                            name: description,
                            productId,
                            partNumber,
                            href,
                            price,
                            badge,
                            onSale: false,
                            consideredBadge,
                        }}
                    />
                </WithLink>
            )}
        </>
    );
};

RecommendationsProduct.propTypes = {
    recommendedProduct: PropTypes.object,
    index: PropTypes.number,
    messages: PropTypes.shape({
        wearItWith: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
        youMayAlsoLike: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
    }),
    tracking: PropTypes.func,
    onClick: PropTypes.func.isRequired,
    addToBagState: PropTypes.object,
    addToCart: PropTypes.func,
    type: PropTypes.string,
    modifier: PropTypes.string,
    isWithAddToBag: PropTypes.bool,
    withQuickAdd: PropTypes.bool,
    isLastAdded: PropTypes.bool,
    inProgress: PropTypes.bool,
    isWishlistRecommendations: PropTypes.bool,
    overlayProductId: PropTypes.string,
    handleOverlayProductIdChange: PropTypes.func,
    drawerTracking: PropTypes.func,
    setIsPhoneOverlay: PropTypes.func,
    doubleImageWithoutPadding: PropTypes.string,
    positionDrawer: PropTypes.func,
    reducedMode: PropTypes.bool,
    details: PropTypes.bool,
    omnibusAlignment: PropTypes.bool,
    onRemove: PropTypes.func,
};

RecommendationsProduct.defaultProps = {
    recommendedProduct: {},
    index: 0,
    messages: {},
};

export default RecommendationsProduct;
